/** SUNTRONIX ONE — Mobile Filter Sheet.
 *
 *  Replaces the inline desktop filter bar with a bottom sheet on
 *  small viewports (< md).  The parent renders its filter controls
 *  as children — this component only handles the trigger + sheet
 *  chrome + active-filter counting.
 */
import { useState } from "react";
import { Filter, X } from "lucide-react";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

export default function MobileFilterSheet({
  activeCount = 0,
  children,
  onApply,
  onClear,
  testId = "mobile-filter-sheet",
  triggerLabel = "Filters",
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testId}-trigger`}
        className="lg:hidden h-10 px-3 rounded-md border border-slate-200 bg-white text-[13px] font-medium text-slate-900 hover:bg-slate-50 flex items-center gap-1.5 min-h-[40px]"
      >
        <Filter className="h-4 w-4 text-slate-500" strokeWidth={1.6} />
        <span>{triggerLabel}</span>
        {activeCount > 0 && (
          <span
            data-testid={`${testId}-badge`}
            className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-slate-900 text-white text-[10px] font-semibold grid place-items-center"
          >
            {activeCount}
          </span>
        )}
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          data-testid={testId}
          className="rounded-t-2xl p-0 max-h-[85dvh] flex flex-col"
        >
          <SheetHeader className="px-5 pt-4 pb-3 text-left border-b border-slate-100 shrink-0">
            <div className="mx-auto h-1 w-10 rounded-full bg-slate-300 mb-3" aria-hidden="true" />
            <div className="flex items-center justify-between">
              <SheetTitle className="text-[16px] font-heading font-semibold text-slate-900">
                {triggerLabel}
              </SheetTitle>
              <button
                onClick={() => setOpen(false)}
                data-testid={`${testId}-close`}
                aria-label="Close filters"
                className="h-8 w-8 grid place-items-center rounded-md text-slate-500 hover:bg-slate-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {children}
          </div>

          <SheetFooter className="px-5 py-3 border-t border-slate-100 shrink-0 flex flex-row items-center justify-between gap-2 sm:justify-between">
            <Button
              variant="ghost"
              onClick={() => { onClear?.(); }}
              data-testid={`${testId}-clear`}
              className="text-slate-600"
            >
              Clear all
            </Button>
            <Button
              onClick={() => { onApply?.(); setOpen(false); }}
              data-testid={`${testId}-apply`}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Apply filters
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
